import { formatAddress, isResource } from '@medplum/core'
import type {
  Coding,
  ContactPoint,
  List,
  Period,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact, kebabCase, uniq } from 'lodash-es'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  ASTRANA_NETWORK_IPA_SYSTEM,
  ASTRANA_PORTAL_PROVIDER_ID_SYSTEM,
  GROUP_TYPE_SYSTEM,
  NON_NETWORK_TAG_CODE,
  NPI_SYSTEM,
  NUCC_PROVIDER_TAXONOMY_SYSTEM,
  PRACTITIONER_PREFERRED_SPECIALTY_SYSTEM,
  PRACTITIONER_RECENT_SELECTIONS_SYSTEM,
  PRACTITIONER_ROLE,
  PRACTITIONER_SERVICE_CATEGORY_SYSTEM,
  PROVIDER_NETWORK_STATUS_DISPLAY,
  PROVIDER_NETWORK_STATUS_SYSTEM,
  ProviderNetworkStatus,
} from '#src/codeSystems.ts'
import { Extension, Tag } from '#src/decorators.ts'
import { Group } from '#src/groups.ts'
import { type IDTRole, isIDTRole } from '#src/models/care-teams/constants.ts'
import { getFaxNumbers } from '#src/models/patients/patients.ts'
import { ExtendedProfile } from '#src/models/profile.ts'
import {
  specialtyConceptForValue,
  specialtyDisplay,
  specialtyValue,
} from '#src/models/specialties.ts'
import { ROLES, type Role } from '#src/roles.ts'
import type { QueryTypes } from '#src/search.ts'
import { PRACTITIONER_SCHEDULING_PREFERENCES_SYSTEM } from '#src/structureDefinitions.ts'
import {
  capitalize,
  formatHumanName,
  getAllNicknames,
  isOfType,
  officialNameOrFallback,
} from '#src/utils.ts'

// Provider/service categories for CRM classification, grounded in the NUCC
// Health Care Provider Taxonomy top-level Groupings — the US national standard
// tied to NPI. Displays are the official NUCC Grouping names.
// Source: NUCC Health Care Provider Taxonomy v25.1. Taxonomy © American Medical
// Association. https://www.nucc.org/
export const ServiceCategory = {
  'allopathic-osteopathic-physicians': {
    code: 'allopathic-osteopathic-physicians',
    display: 'Allopathic & Osteopathic Physicians',
  },
  'physician-assistants-advanced-practice-nursing': {
    code: 'physician-assistants-advanced-practice-nursing',
    display: 'Physician Assistants & Advanced Practice Nursing Providers',
  },
  'behavioral-health-social-services': {
    code: 'behavioral-health-social-services',
    display: 'Behavioral Health & Social Service Providers',
  },
  'pharmacy-services': {
    code: 'pharmacy-services',
    display: 'Pharmacy Service Providers',
  },
  'nursing-services': {
    code: 'nursing-services',
    display: 'Nursing Service Providers',
  },
  'rehabilitative-services': {
    code: 'rehabilitative-services',
    display:
      'Respiratory, Developmental, Rehabilitative & Restorative Service Providers',
  },
  'eye-vision-services': {
    code: 'eye-vision-services',
    display: 'Eye and Vision Services Providers',
  },
  'dental-providers': {
    code: 'dental-providers',
    display: 'Dental Providers',
  },
  'ambulatory-facilities': {
    code: 'ambulatory-facilities',
    display: 'Ambulatory Health Care Facilities',
  },
  hospitals: { code: 'hospitals', display: 'Hospitals' },
  laboratories: { code: 'laboratories', display: 'Laboratories' },
  'nursing-custodial-facilities': {
    code: 'nursing-custodial-facilities',
    display: 'Nursing & Custodial Care Facilities',
  },
  suppliers: { code: 'suppliers', display: 'Suppliers' },
} as const satisfies Record<string, { code: string; display: string }>

export type ServiceCategoryCode = keyof typeof ServiceCategory

export const NETWORK_CONTRACTS = ['Direct', 'Astrana'] as const

export type NetworkContract = (typeof NETWORK_CONTRACTS)[number]

export class ExtendedPractitioner extends ExtendedProfile<Practitioner> {
  static readonly RESOURCE_TYPE = 'Practitioner'

  static isInstance(value: unknown): value is Practitioner {
    return isResource(value) && isOfType(value, 'Practitioner')
  }

  static queryByEmail(email: string): QueryTypes {
    return {
      email: email.trim().toLowerCase(),
    }
  }

  static queryByRoles(...roles: Role[]): QueryTypes {
    return {
      active: true,
      identifier:
        roles.length > 0
          ? roles.map((role) => [`${PRACTITIONER_ROLE}|${role}`]).join(',')
          : `${PRACTITIONER_ROLE}|`,
      _sort: ['family', '-_lastUpdated'],
      _count: '1000',
    }
  }

  static queryByGroups(...groups: Group[]): QueryTypes {
    return {
      active: true,
      identifier:
        groups.length > 0
          ? groups.map((group) => `${GROUP_TYPE_SYSTEM}|${group}`).join(',')
          : `${GROUP_TYPE_SYSTEM}|`,
      _sort: ['family', '-_lastUpdated'],
      _count: '1000',
    }
  }

  // Practitioners qualified in any of these NUCC specialties.
  static byQualifications(specialties: readonly Coding[]): QueryTypes {
    return {
      'qualification-code': specialties
        .map((coding) => `${NUCC_PROVIDER_TAXONOMY_SYSTEM}|${coding.code}`)
        .join(','),
    }
  }

  static queryByIDTRoles(idtRoles: readonly IDTRole[] = []): QueryTypes {
    return {
      ...this.queryByRoles(...idtRoles),
    }
  }

  // Non-excluding variant for the fax recipient list and admin provider
  // catalog, where non-network practitioners must stay visible.
  static faxRecipientSearchQuery(): QueryTypes {
    return {
      active: true,
      _sort: ['family', '-_lastUpdated'],
      _count: '1000',
    }
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      ...ExtendedPractitioner.faxRecipientSearchQuery(),
      '_tag:not': `${PROVIDER_NETWORK_STATUS_SYSTEM}|${NON_NETWORK_TAG_CODE}`,
    }
  }

  static allInternalSearchQuery(name?: string): QueryTypes {
    const query: QueryTypes = {
      active: true,
      _sort: ['family', '-_lastUpdated'],
      _count: '1000',
      // Internal = carries a PRACTITIONER_ROLE identifier (Seen staff). This is
      // the `internal` network-status definition, and holds without depending on
      // the backfilled status tag — so it is correct in any data state.
      identifier: `${PRACTITIONER_ROLE}|`,
    }

    if (name) {
      query['name:contains'] = name
    }

    return query
  }

  // Same internal-practitioner query, without filtering by active status.
  static allInternalIncludingInactiveSearchQuery(name?: string): QueryTypes {
    const { active: _active, ...query } = this.allInternalSearchQuery(name)
    return query
  }

  get isInternal(): boolean {
    return (
      (this.primaryEmail?.endsWith('@seenhealth.org') ||
        this.primaryEmail?.endsWith('@seensgv.com')) ??
      false
    )
  }

  get groups(): Group[] | undefined {
    return this.resource.identifier
      ?.filter((i) => i.system === GROUP_TYPE_SYSTEM)
      .map((i) => i.value as Group)
  }

  set groups(groups: Group[] | undefined) {
    this.resource.identifier = [
      ...(this.resource.identifier ?? []).filter(
        (i) => i.system !== GROUP_TYPE_SYSTEM
      ),
      ...(groups ?? []).map((group) => ({
        system: GROUP_TYPE_SYSTEM,
        value: group,
      })),
    ]
  }

  get npi(): string | undefined {
    return this.identifier(NPI_SYSTEM)?.value
  }

  set npi(npi: string | undefined) {
    if (npi) {
      this.setIdentifier(NPI_SYSTEM, npi)
    } else {
      this.removeIdentifier(NPI_SYSTEM)
    }
  }

  get astranaPortalProviderIds(): string[] {
    return compact(
      this.identifiers(ASTRANA_PORTAL_PROVIDER_ID_SYSTEM).map((id) => id.value)
    )
  }

  /**
   * Records an Astrana provider-portal id on this practitioner, accumulating
   * instead of overwriting.
   *
   * Several Astrana provider records — each with its own provider key id — can
   * resolve to one Medplum Practitioner because the authorization import matches
   * by shared NPI/EZCAP/name, not by the portal id. The previous code used
   * `setIdentifier`, which is single-valued per system, so two such records
   * ping-ponged the identifier A→B→A→B on every import pass (~100 writes/day).
   *
   * This is purely additive and idempotent: once a value is present the call is
   * a no-op, so re-runs produce zero JSON-patch ops and stop writing entirely.
   */
  addAstranaPortalProviderId(astranaProviderId: string): void {
    if (this.astranaPortalProviderIds.includes(astranaProviderId)) {
      return
    }
    this.resource.identifier ??= []
    this.resource.identifier.push({
      system: ASTRANA_PORTAL_PROVIDER_ID_SYSTEM,
      value: astranaProviderId,
    })
  }

  get telecom(): Practitioner['telecom'] {
    return this.resource.telecom
  }

  set telecom(telecom: Practitioner['telecom']) {
    this.resource.telecom = telecom
  }

  addTelecom(telecom: ContactPoint): void {
    this.resource.telecom ??= []
    this.resource.telecom.push(telecom)
  }

  // All fax numbers on file, not just primaryFaxNumber — used by the Send Fax
  // recipient picker, which shows one row per (provider, fax number) pair.
  faxNumbers(): string[] {
    return getFaxNumbers(this.resource)
  }

  isIDT(): boolean {
    const roles = this.roles
    return !!roles && roles.some((role) => isIDTRole(role))
  }

  isTech(): boolean {
    return !!this.groups?.some((group) => group === Group.TECH)
  }

  get roles(): Role[] | undefined {
    return this.resource.identifier
      ?.filter((i) => i.system === PRACTITIONER_ROLE)
      .map((i) => i.value as Role)
  }

  set roles(roles: Role[] | undefined) {
    this.resource.identifier = [
      ...(this.resource.identifier ?? []).filter(
        (i) => i.system !== PRACTITIONER_ROLE
      ),
      ...(roles ?? []).map((role) => ({
        system: PRACTITIONER_ROLE,
        value: role,
      })),
    ]
  }

  get idtRoles(): IDTRole[] | undefined {
    return this.roles?.filter((role) => isIDTRole(role))
  }

  get rolesDisplay(): string | undefined {
    if (!this.roles) {
      return undefined
    }
    return this.roles
      .map((role) => displayFromCodeableConcept(ROLES[role]))
      .join(', ')
  }

  get rolesInitials(): string | undefined {
    if (!this.roles) {
      return undefined
    }
    return this.roles.join(', ')
  }

  isRole(...roles: Role[]): boolean {
    const practitionerRoles = this.roles
    return practitionerRoles
      ? roles.some((role) => practitionerRoles.includes(role))
      : false
  }

  get name(): string {
    const name = officialNameOrFallback(this.resource)
    if (!name) {
      return 'Unknown'
    }
    const nicknames = getAllNicknames(this.resource)
    return formatHumanName(name, { prefix: true, nicknames })
  }

  get active(): boolean | undefined {
    return this.resource.active
  }

  set active(active: boolean) {
    this.resource.active = active
  }

  get birthDate(): string | undefined {
    return this.resource.birthDate
  }

  @Extension({
    url: PRACTITIONER_RECENT_SELECTIONS_SYSTEM,
    kind: 'valueReference',
  })
  recentSelections?: Reference<List>

  @Extension({
    url: PRACTITIONER_SCHEDULING_PREFERENCES_SYSTEM,
    kind: 'valueString',
  })
  private _schedulingPreferences?: string

  // Free-text natural-language scheduling rules & preferences fed to the
  // AI slot-suggestion agent.
  get schedulingPreferences(): string | undefined {
    return this._schedulingPreferences
  }

  set schedulingPreferences(value: string | undefined) {
    this._schedulingPreferences = value
  }

  @Tag({ system: PRACTITIONER_SERVICE_CATEGORY_SYSTEM })
  _serviceCategory?: Coding

  get serviceCategory(): ServiceCategoryCode | undefined {
    return this._serviceCategory?.code as ServiceCategoryCode | undefined
  }

  set serviceCategory(code: ServiceCategoryCode | undefined) {
    if (!code) {
      this._serviceCategory = undefined
      return
    }
    this._serviceCategory = {
      system: PRACTITIONER_SERVICE_CATEGORY_SYSTEM,
      code,
      display: ServiceCategory[code].display,
    }
  }

  @Tag({
    system: PRACTITIONER_PREFERRED_SPECIALTY_SYSTEM,
    allowMultiple: true,
  })
  _preferredForSpecialties?: Coding[]

  get preferredForSpecialties(): string[] {
    return compact(
      (this._preferredForSpecialties ?? []).map((c) => c.display ?? c.code)
    )
  }

  set preferredForSpecialties(specialties: string[]) {
    this._preferredForSpecialties = specialties.map((name) => ({
      system: PRACTITIONER_PREFERRED_SPECIALTY_SYSTEM,
      code: kebabCase(name),
      display: name,
    }))
  }

  get isPreferred(): boolean {
    return (this._preferredForSpecialties ?? []).length > 0
  }

  @Tag({ system: PROVIDER_NETWORK_STATUS_SYSTEM })
  _networkStatus?: Coding

  // Derived clinician-visibility status. Practitioners only ever carry an
  // explicit tag for the legacy non-network case; everything else reads as
  // non-network until the backfill stamps internal/in-network.
  get networkStatus(): ProviderNetworkStatus {
    const code = this._networkStatus?.code
    return code === ProviderNetworkStatus.Internal ||
      code === ProviderNetworkStatus.InNetwork ||
      code === ProviderNetworkStatus.NonNetwork
      ? code
      : ProviderNetworkStatus.NonNetwork
  }

  set networkStatus(status: ProviderNetworkStatus | undefined) {
    this._networkStatus = status
      ? {
          system: PROVIDER_NETWORK_STATUS_SYSTEM,
          code: status,
          display: PROVIDER_NETWORK_STATUS_DISPLAY[status],
        }
      : undefined
  }

  get isNonNetwork(): boolean {
    return this._networkStatus?.code === NON_NETWORK_TAG_CODE
  }

  get networkContract(): NetworkContract {
    return this.identifier(ASTRANA_NETWORK_IPA_SYSTEM) ? 'Astrana' : 'Direct'
  }

  get qualifications(): Practitioner['qualification'] {
    return this.resource.qualification
  }

  get qualificationNames(): string[] {
    return uniq(
      compact(
        this.qualifications?.flatMap((q) =>
          capitalize(q.code.text?.trim() ?? '')
        ) ?? []
      )
    )
  }

  addQualification(qualification: string): void {
    const qualifications = (this.resource.qualification ?? []).filter(
      (q) =>
        q.code.text?.toLowerCase()?.trim() !==
        qualification.toLowerCase().trim()
    )
    this.resource.qualification = [
      ...qualifications,
      {
        code: {
          coding: [
            {
              code: kebabCase(qualification),
            },
          ],
          text: qualification,
        },
      },
    ]
  }

  set qualifications(qualifications: string[]) {
    // Keep the full existing entry (period, issuer, identifier, ...) for names
    // still selected; only newly added names get a bare entry.
    const existing = this.resource.qualification ?? []
    const next = qualifications.map(
      (name) =>
        existing.find(
          (q) =>
            q.code.text?.toLowerCase()?.trim() === name.toLowerCase().trim()
        ) ?? {
          code: {
            coding: [
              {
                code: kebabCase(name),
              },
            ],
            text: name,
          },
        }
    )
    // Empty FHIR arrays fail validation; clear the field instead.
    this.resource.qualification = next.length > 0 ? next : undefined
  }

  // Specialty picker values: the NUCC taxonomy code when the qualification is
  // coded (or its text resolves through the specialty vocabulary), else the
  // raw text. See models/specialties.ts.
  get specialtyValues(): string[] {
    return uniq(
      compact((this.qualifications ?? []).map((q) => specialtyValue(q.code)))
    )
  }

  set specialtyValues(values: string[]) {
    // Keep the full existing entry (period, issuer, identifier, ...) for
    // values still selected; new values get a NUCC-coded (or text-only) entry.
    const existing = this.resource.qualification ?? []
    const next = values.map(
      (value) =>
        existing.find((q) => specialtyValue(q.code) === value) ?? {
          code: specialtyConceptForValue(value),
        }
    )
    // Empty FHIR arrays fail validation; clear the field instead.
    this.resource.qualification = next.length > 0 ? next : undefined
  }

  get specialtyDisplays(): string[] {
    return this.specialtyValues.map(specialtyDisplay)
  }

  // Sets qualifications directly from taxonomy codings, preserving each coding's
  // exact code/system. Unlike `specialtyValues`, this does not resolve values
  // through the curated specialty vocabulary, so NUCC codes outside that subset
  // (e.g. registry-sourced subspecialties) survive intact.
  set qualificationCodings(codings: Coding[]) {
    // Empty FHIR arrays fail validation; clear the field instead.
    this.resource.qualification =
      codings.length > 0
        ? codings.map((coding) => ({
            code: { coding: [coding], text: coding.display },
          }))
        : undefined
  }

  hasQualification(name: string): boolean {
    return this.qualificationNames
      .map((q) => q.toLowerCase())
      .includes(name.trim().toLowerCase())
  }

  get qualificationSummary(): { code: string; period: Period | undefined }[] {
    return (
      this.resource.qualification?.map((q) => ({
        code: displayFromCodeableConcept(q.code) ?? q.code.text ?? '',
        period: q.period,
      })) ?? []
    )
  }

  get identifierSummary(): { type: string; system?: string; value?: string }[] {
    return (
      this.resource.identifier?.map((i) => ({
        type: codeFromCodeableConcept(i.type) ?? '',
        system: i.system,
        value: i.value,
      })) ?? []
    )
  }

  protected getExportFields() {
    return {
      id: this.id,
      active: this.active,
      firstName: this.firstName,
      lastName: this.lastName,
      gender: this.gender,
      birthDate: this.birthDate,
      telecom: this.telecomSummary,
      qualifications: this.qualificationSummary,
      identifier: this.identifierSummary,
      isIDT: this.isIDT(),
      email: this.primaryEmail,
      phone: this.primaryPhoneNumber,
      address: this.primaryAddress,
      roles: this.roles,
      groups: this.groups,
    }
  }

  get searchKeywords(): string[] {
    return compact([
      this.name,
      this.nickname,
      this.rolesInitials,
      this.rolesDisplay,
      this.primaryFaxNumber,
      this.primaryPhoneNumber,
      this.primaryAddress && formatAddress(this.primaryAddress),
      ...this.specialtyDisplays,
      ...this.qualificationNames,
      this.npi,
    ])
  }

  _addIdentifier(system: string, value: string): void {
    this.addIdentifier(system, value)
  }
}

export type PractitionerReferenceString = string
